import React, { useCallback, useRef, useState } from 'react';
import styles from './styles.scss';
import classNames from 'classnames/bind';
import Icon from 'Components/Icon';
import { v4 } from 'uuid';

const cx = classNames.bind(styles);

export const WEEKDAYS_LONG = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
];

export const isDateValid = (dateStr) => !isNaN(new Date(dateStr));

const InputDate = ({
  className,
  disabledDays = [],
  disabledWeekdays = [],
  errorMessage = '',
  pastMessage = '',
  label = '',
  ariaLabel,
  min,
  name = '',
  onChange = () => {},
  setError = () => {},
  value,
}) => {
  const uniqueId = `input-date-${v4()}`;
  const inputRef = useRef();
  const [internalValue, setInternalValue] = useState(value ? value.toISOString().slice(0, 10) : '');
  const [internalError, setInternalError] = useState(false);
  const [internalErrorMessage, setInternalErrorMessage] = useState(false);

  const handleFocus = useCallback(() => {
    const input = inputRef.current;
    if (input?.type === 'date') {
      try {
        input?.showPicker();
      } catch (e) {}
    }
  }, []);

  const handleBlur = useCallback((e) => {
    const enteredValue = e.target.value;

    if (enteredValue === '') {
      // undefined state: if you enter only one date part,
      // it is still considered to be empty but the falsy part is kept
      e.target.value = '';
      return;
    }

    if (!isDateValid(enteredValue)) {
      setInternalError(true);
      setInternalErrorMessage('Ungültiger Wert');
      window.setTimeout(() => {
        setInternalError(false);
        setInternalErrorMessage('');
      }, 3000);
    }
  }, []);

  const handleChange = useCallback((e) => {
    const enteredValue = e.target.value;
    if (!isDateValid(enteredValue)) {
      return;
    }
    const selectedDate = new Date(enteredValue);
    const weekday = selectedDate.getDay();
    const isPast = min ? selectedDate < new Date(min) : false;
    const currentErrorMessage = isPast ? pastMessage || errorMessage : errorMessage;
    const isoValue = selectedDate.toISOString().slice(0, 10);
    setInternalValue(isoValue);

    if (disabledWeekdays.includes(weekday) || disabledDays.includes(isoValue) || isPast) {
      setInternalError(true);
      setError(currentErrorMessage);
      setInternalErrorMessage(currentErrorMessage);
      onChange(undefined);
    } else {
      setInternalError(false);
      setError('');
      onChange(selectedDate);
    }
  }, []);

  return (
    <div
      className={cx(
        className,
        styles.input,
        styles.calendarWrapper,
        internalError && styles.invalid
      )}
    >
      <div className={styles.label}>{label}</div>
      <input
        name={name}
        type="date"
        value={internalValue}
        min={min}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-label={ariaLabel || label}
        ref={inputRef}
        id={uniqueId}
      />
      <button
        onClick={handleFocus}
        className={cx(styles.icon, internalError && styles.iconError)}
        aria-controls={uniqueId}
        aria-label="Kalender öffnen"
      >
        <Icon name="calendar" />
      </button>
      {internalError && (
        <div className={styles.errorMessage} role="alert" aria-live="assertive">
          {internalErrorMessage}
        </div>
      )}
    </div>
  );
};

export default InputDate;
